import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { paymentApi } from '../services/api';
import { useAuthStore } from '../store/authStore';

function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | failed
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const paymentType = searchParams.get('type');

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    const paymentRequestId = searchParams.get('payment_request_id');
    const paymentId = searchParams.get('payment_id');
    const paymentStatus = searchParams.get('payment_status');

    if (!paymentRequestId || !paymentId) {
      setStatus('failed');
      setError('Invalid payment return URL. Missing payment details.');
      return;
    }

    // If Instamojo already says it failed, show error
    if (paymentStatus && paymentStatus !== 'Credit') {
      setStatus('failed');
      setError('Payment was not completed. Please try again.');
      return;
    }

    try {
      const res = await paymentApi.verifyPayment(paymentRequestId, paymentId);
      setResult(res.data);
      setStatus('success');

      // If registration payment, update auth store
      if (res.data.registrationComplete) {
        const wasOnboarded = useAuthStore.getState().user?.onboardingCompleted;
        useAuthStore.setState(state => ({
          user: { ...state.user, registrationPaid: true, onboardingCompleted: wasOnboarded || false }
        }));
        // Track if user was already onboarded (came from recharge page)
        res.data._fromRecharge = wasOnboarded;
      }
    } catch (err) {
      setStatus('failed');
      setError(err.response?.data?.error || 'Payment verification failed. Please contact support.');
    }
  };

  const isRegistration = paymentType === 'registration' || result?.registrationComplete;
  const isOnboarding = result?.studentId;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">

        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-primary-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-500">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {isOnboarding ? 'Student Onboarded!' : isRegistration ? 'Registration Complete!' : 'Payment Successful!'}
            </h2>
            {result?.questionsAdded && (
              <p className="text-gray-600 mb-1">
                <span className="font-bold text-green-600">{result.questionsAdded}</span> questions added to your account
              </p>
            )}
            {result?.amount && (
              <p className="text-gray-500 text-sm mb-6">Amount paid: ₹{result.amount}</p>
            )}
            {isOnboarding && (
              <p className="text-gray-600 mb-6">
                Student has been created successfully. They will receive their login credentials via email.
              </p>
            )}
            {isRegistration && !result?._fromRecharge && !isOnboarding && (
              <p className="text-gray-600 mb-6">
                Welcome to Memory Coach! Your account is now active.
              </p>
            )}
            {isRegistration && result?._fromRecharge && (
              <p className="text-gray-600 mb-6">
                Registration complete! You can now purchase question credits.
              </p>
            )}
            <button
              onClick={() => {
                if (isOnboarding) {
                  navigate('/trainer', { replace: true });
                } else if (isRegistration && result?._fromRecharge) {
                  navigate('/chat', { replace: true });
                } else if (isRegistration) {
                  navigate('/chat', { replace: true });
                } else {
                  navigate('/chat', { replace: true });
                }
              }}
              className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              {isOnboarding
                ? 'Back to Dashboard'
                : isRegistration
                  ? 'Start Using Memory Coach'
                  : 'Back to Chat'
              }
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate(isRegistration ? '/payment' : '/recharge')}
                className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
              <p className="text-xs text-gray-400">
                If money was deducted, it will be refunded within 5-7 business days.
                Contact support if you need help.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentReturn;
